import React, { useState } from 'react';
import { Spin } from 'antd';
import engagementService from '../../../../../../../services/engagement/service';

import {
  EngagementWrapper,
  FeaturesScrollContainer,
  FeatureCard,
  FeatureContent,
  FeatureImage,
  FeatureTitle,
  FeatureDescription,
  FeaturePrice,
  BookNowButton,
  ThemedSelect,
} from './styled';

/**
 * Convert the raw data (item.engagementData) into an array of features
 * that we can render. We'll store an array for possible shoutout types,
 * and a list of platform options for each feature.
 */
const extractFeaturesFromProduct = (data = {}) => {
  const features = [];

  if (data.enableShoutouts) {
    // NOTE: The creator can specify e.g. shoutoutsTypeOptions: ["story","page"] or ["story","page","both"].
    // We'll pass that to the feature so we can build the dropdown dynamically.
    features.push({
      key: 'shoutouts',
      title: data.shoutoutsTitle || 'Shoutouts',
      description: data.shoutoutsDescription || '',
      priceStory: data.shoutoutsPriceStory || 0,
      pricePage: data.shoutoutsPricePage || 0,
      // Single price if you prefer:
      // price: data.shoutoutsPrice || 0,

      // The types the creator wants to offer
      shoutoutsTypeOptions: data.shoutoutsType === 'both'
        ? ['story', 'page']
        : [data.shoutoutsType],
      // e.g. ["story","page","both"], or just ["story","page"], etc.

      shoutoutsType: data.shoutoutsType,

      // The platforms the creator wants to offer
      shoutoutsPlatforms: data.shoutoutsPlatforms || ['instagram'],
      image: Array.isArray(data.shoutoutsImage) ? data.shoutoutsImage[0]?.url : data.shoutoutsImage,
    });
  }

  if (data.enableFollows) {
    features.push({
      key: 'follows',
      title: data.followsTitle || 'Follows',
      description: data.followsDescription || '',
      price: data.followsPrice,
      image: Array.isArray(data.followsImage) ? data.followsImage[0]?.url : data.followsImage,
    });
  }

  if (data.enableComments) {
    features.push({
      key: 'comments',
      title: data.commentsTitle || 'Comments',
      description: data.commentsDescription || '',
      price: data.commentsPrice,
      image: Array.isArray(data.commentsImage) ? data.commentsImage[0]?.url : data.commentsImage,
    });
  }

  if (data.enableLikes) {
    features.push({
      key: 'likes',
      title: data.likesTitle || 'Likes',
      description: data.likesDescription || '',
      price: data.likesPrice,
      image: Array.isArray(data.likesImage) ? data.likesImage[0]?.url : data.likesImage,
    });
  }

  return features;
};

/**
 * Child component for rendering the Shoutouts card. We move useState here
 * so we don't violate Hooks rules by calling them in a .map() loop.
 */
function ShoutoutsFeatureCard({ feature, theme, isLoading, onBuyNow }) {

  const initialType = feature.shoutoutsType === 'both'
    ? 'story'  // Default to story when both are available
    : feature.shoutoutsType

  const [selectedType, setSelectedType] = useState(initialType);
  const [selectedPlatform, setSelectedPlatform] = useState(feature.shoutoutsPlatforms?.[0] || 'instagram');



  // Build the array of type <Select> options from the creator's config:
  const typeOptions = feature.shoutoutsType === 'both'
    ? [
      { label: 'Story Post', value: 'story' },
      { label: 'Page Post', value: 'page' }
    ]
    : [
      {
        label: feature.shoutoutsType === 'story' ? 'Story Post' : 'Page Post',
        value: feature.shoutoutsType
      }
    ];

  // Build the array of platform <Select> options from the creator's config:
  const platformOptions = (feature.shoutoutsPlatforms || []).map((p) => ({
    label: p.charAt(0).toUpperCase() + p.slice(1),
    value: p
  }));

  // Calculate final price if you store separate story/page
  let finalPrice = selectedType === 'story'
    ? feature.priceStory
    : feature.pricePage;

  const hasImage = Boolean(feature.image);

  const priceDisplay = feature.shoutoutsType === 'both'
    ? (
      <div>
        <FeaturePrice theme={theme}>
          ${Number(finalPrice || 0).toFixed(2)}
        </FeaturePrice>
      </div>
    )
    : (
      <FeaturePrice theme={theme}>
        ${Number(finalPrice || 0).toFixed(2)}
      </FeaturePrice>
    );

  return (
    <FeatureCard theme={theme}>
      <FeatureContent hasImage={hasImage}>
        {/* Title */}
        <FeatureTitle theme={theme}>{feature.descriptionTitle}</FeatureTitle>

        {/* Description */}
        <FeatureDescription theme={theme}>{feature.description}</FeatureDescription>

        {/* Row of dropdowns: Type (left), Platform (right) */}
        <div style={{ display: 'flex', gap: '8px', margin: '8px 0' }}>
          <ThemedSelect
            style={{ flex: 1 }}
            value={selectedType}
            onChange={(val) => setSelectedType(val)}
            options={typeOptions}
          />

          <ThemedSelect
            style={{ flex: 1 }}
            value={selectedPlatform}
            onChange={(val) => setSelectedPlatform(val)}
            options={platformOptions}
          />
        </div>

        {priceDisplay}

        {/* Buy Now Button */}
        <BookNowButton
          theme={theme}
          disabled={isLoading}
          onClick={() =>
            onBuyNow({
              shoutoutType: selectedType,
              shoutoutPlatform: selectedPlatform,
            })
          }
        >
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Spin size="small" />
              <span style={{ marginLeft: 8 }}>Processing...</span>
            </div>
          ) : (
            'Buy Now'
          )}
        </BookNowButton>
      </FeatureContent>

      {/* Optional Image */}
      {hasImage && (
        <FeatureImage>
          <img src={feature.image} alt={feature.title} />
        </FeatureImage>
      )}
    </FeatureCard>
  );
}

/**
 * Main EngagementCardComponent
 */
const EngagementCardComponent = ({ item, theme = {}, storeName }) => {
  const [loadingFeatureKey, setLoadingFeatureKey] = useState(null);
  const features = extractFeaturesFromProduct(item.engagementData || {});

  // Generic buy now handler
  const handleBuyNow = async (featureKey, extraData = {}) => {
    setLoadingFeatureKey(featureKey);
    try {
      const { url } = await engagementService.createCheckoutSession({
        productId: item.id,
        storeName,
        featureKey,
        ...extraData, // pass the user's chosen type/platform
      });
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setLoadingFeatureKey(null);
    }
  };
  return (
    <EngagementWrapper theme={theme}>
      <h2 className="engagement-title">{item.engagementData?.title || item.text || item.title || 'My Engagement Packages'}</h2>
      {(item.engagementData?.subtitle || item.subtitle) && (
        <div
          className="engagement-description"
          dangerouslySetInnerHTML={{ __html: item.engagementData?.subtitle || item.subtitle }}
        />
      )}

      <FeaturesScrollContainer theme={theme}>
        {features.map((feature) => {
          const isLoading = loadingFeatureKey === feature.key;
          const hasImage = Boolean(feature.image);

          // Non-shoutout features
          if (feature.key !== 'shoutouts') {
            return (
              <FeatureCard key={feature.key} theme={theme}>
                <FeatureContent hasImage={hasImage}>
                  <div>
                    <FeatureTitle theme={theme}>{feature.title}</FeatureTitle>
                    <FeatureDescription theme={theme}>{feature.description}</FeatureDescription>
                    <FeaturePrice theme={theme}>${feature.price || '0'}</FeaturePrice>
                  </div>

                  <BookNowButton
                    theme={theme}
                    disabled={isLoading}
                    onClick={() => handleBuyNow(feature.key)}
                  >
                    {isLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Spin size="small" />
                        <span style={{ marginLeft: 8 }}>Processing...</span>
                      </div>
                    ) : (
                      'Buy Now'
                    )}
                  </BookNowButton>
                </FeatureContent>

                {hasImage && (
                  <FeatureImage>
                    <img src={feature.image} alt={feature.title} />
                  </FeatureImage>
                )}
              </FeatureCard>
            );
          }

          // Shoutouts
          return (
            <ShoutoutsFeatureCard
              key={feature.key}
              feature={feature}
              theme={theme}
              isLoading={isLoading}
              onBuyNow={(extra) => handleBuyNow(feature.key, extra)}
            />
          );
        })}
      </FeaturesScrollContainer>
    </EngagementWrapper>
  );
};

export default EngagementCardComponent;
